import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card, CardContent, CardHeader, CardTitle } from './ui/card'
import { Button } from './ui/button'
import { Input } from './ui/input'
import { RefreshCw, Download } from 'lucide-react'
import { useUserContext } from '../contexts/UserContext'
import DataTable from './common/DataTable'
import { generateColumns, filterColumns } from '../utils/tableUtils'
import { UserProfile, UserQueryState } from '../types/user'
import { Spinner } from './ui/spinner'
import { usePersistedState } from '../hooks/usePersistedState'
import { AlertView } from './common/Alert'
import { ColumnDef } from '@tanstack/react-table'
import { useCSVDownload } from '../hooks/useCSVDownload'
import { DataTableSkeleton } from './TableSkeleton'
import { useTrackNavigation } from '../hooks/useTrackNavigation'
import { validateEmail, isValidUUID } from '../utils/sanitizeInput'

type UserFilters = Pick<UserQueryState, 'phoneNumber' | 'email' | 'uuid'>

// Paging and the table's own text filter never change which users the API
// returned, so only the lookup fields decide whether the form has drifted from
// the submitted search.
const sameFilters = (left: UserFilters, right: UserFilters): boolean =>
    left.phoneNumber === right.phoneNumber && left.email === right.email && left.uuid === right.uuid

const isUserFilters = (value: unknown): value is UserFilters => {
    if (!value || typeof value !== 'object') return false
    const filters = value as UserFilters
    return ['phoneNumber', 'email', 'uuid'].every(key => typeof filters[key as keyof UserFilters] === 'string')
}

const isStoredQueryState = (value: unknown): value is UserQueryState => {
    if (!isUserFilters(value)) return false
    const state = value as UserQueryState
    return Number.isInteger(state.currentPage) && state.currentPage > 0 &&
        [10, 50].includes(state.itemsPerPage) && typeof state.tableFilter === 'string' &&
        Array.isArray(state.filteredUsers) &&
        (state.submittedFilters == null || isUserFilters(state.submittedFilters))
}

const ProfileComponent = (): JSX.Element => {
    const navigate = useNavigate()
    const { data: users, loading, error, fetchData: fetchUsers, clearData: clearUsers } = useUserContext()
    const { isDownloading, handleDownload } = useCSVDownload<UserProfile>()
    useTrackNavigation()

    const getDefaultState = (): UserQueryState => ({
        searchTerm: '',
        currentPage: 1,
        itemsPerPage: 10,
        phoneNumber: '',
        email: '',
        users: [],
        filteredUsers: users,
        tableFilter: '',
        uuid: '',
        submittedFilters: null
    })

    const [queryState, setQueryState] = usePersistedState<UserQueryState>({
        storageKey: 'UserQueryState',
        defaultValue: getDefaultState(),
        validate: isStoredQueryState
    })
    const submittedFilters = queryState.submittedFilters ?? null
    const phoneError = queryState.phoneNumber && !/^\d{10}$/.test(queryState.phoneNumber)
        ? 'Phone number must be 10 digits' : ''
    const emailError = queryState.email && !validateEmail(queryState.email)
        ? 'Please enter a valid email address' : ''
    const uuidError = !queryState.uuid ? '' : queryState.uuid.length !== 20
        ? 'UUID must be 20 characters long' : !isValidUUID(queryState.uuid) ? 'Please enter a valid UUID' : ''
    const [showUnappliedFiltersHint, setShowUnappliedFiltersHint] = useState(false)
    const hasUnappliedFilters = submittedFilters
        ? !sameFilters(queryState, submittedFilters)
        : Boolean(queryState.phoneNumber || queryState.email || queryState.uuid)

    // Any later edit — Find included — hides the hint until Download CSV is
    // pressed again.
    useEffect(() => {
        setShowUnappliedFiltersHint(false)
    }, [queryState.phoneNumber, queryState.email, queryState.uuid])

    const setCurrentPage = useCallback((currentPage: number) =>
        setQueryState(prev => ({ ...prev, currentPage })),
        [setQueryState]
    )

    const setItemsPerPage = useCallback((itemsPerPage: number) =>
        setQueryState(prev => ({ ...prev, itemsPerPage, currentPage: 1 })),
        [setQueryState]
    )

    const setTableFilter = useCallback((tableFilter: string) => {
        setQueryState(prev => ({ ...prev, tableFilter, currentPage: 1 }))
    }, [setQueryState])

    const handlePhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        const digits = e.target.value.replace(/\D/g, '')
        const phoneNumber = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits
        setQueryState(prev => ({ ...prev, phoneNumber, email: '', uuid: '' }))
    }

    const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        const email = e.target.value.trim()
        setQueryState(prev => ({ ...prev, email, phoneNumber: '', uuid: '' }))
    }

    const handleUuidChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        const uuid = e.target.value.trim()
        setQueryState(prev => ({ ...prev, email: '', phoneNumber: '', uuid }))
    }

    const resetToDefaults = () => {
        clearUsers()
        setQueryState(getDefaultState())
        setShowUnappliedFiltersHint(false)
    }

    const submitLookup = (filters: UserFilters) => {
        setShowUnappliedFiltersHint(false)
        setQueryState(prev => ({ ...prev, submittedFilters: filters, tableFilter: '', currentPage: 1 }))
        void fetchUsers(filters)
    }

    const handleFindPhoneNumberClick = () => {
        if (!queryState.phoneNumber || phoneError) return
        submitLookup({ phoneNumber: queryState.phoneNumber, email: '', uuid: '' })
    }

    const handleFindEmailClick = () => {
        if (!queryState.email || emailError) return
        submitLookup({ phoneNumber: '', email: queryState.email, uuid: '' })
    }

    const handleFindUuidClick = () => {
        if (!queryState.uuid || uuidError) return
        submitLookup({ phoneNumber: '', email: '', uuid: queryState.uuid })
    }

    const handleDataChange = useCallback((data: UserProfile[]) => {
        setQueryState(prev => ({ ...prev, filteredUsers: data }))
    }, [setQueryState])

    const handleDownloadCSV = () => {
        // The rows on screen belong to the submitted lookup, so a pending edit
        // would silently export the previous search.
        if (hasUnappliedFilters) {
            setShowUnappliedFiltersHint(true)
            return
        }
        if (loading || error || !users.length || !queryState.filteredUsers.length) return
        handleDownload({
            getData: () => queryState.filteredUsers,
            filename: 'user_data.csv',
            sanitizeData: (users) => users.map(({ user_id, email, phone_number, ...rest }) => rest)
        })
    }

    const columns: ColumnDef<UserProfile, any>[] = useMemo(() => {
        const defaultColumns: ColumnDef<UserProfile, any>[] = [
            { accessorKey: 'user_uuid', header: 'UUID' },
            { accessorKey: 'created_on', header: 'Created On' },
            { accessorKey: 'modified_on', header: 'Modified On' },
            { accessorKey: 'in_corridor', header: 'In Corridor' },
            { accessorKey: 'completed_trips', header: 'Completed Trips' },
            { accessorKey: 'violations', header: 'Violations' },
        ]

        if (!users.length) return defaultColumns

        const allColumns = generateColumns<UserProfile>(users)
        const excludeFields = ['user_id', '_internal_id', 'created_at', 'updated_at', 'signed_up_next_round', 'confirmed', 'inactive', 'phone_number', 'email']
        return filterColumns(allColumns, excludeFields)
    }, [users])

    const paginationState = {
        currentPage: queryState.currentPage,
        itemsPerPage: queryState.itemsPerPage,
        onPageChange: setCurrentPage,
        onItemsPerPageChange: setItemsPerPage
    }

    const tableFilterState = {
        tableFilter: queryState.tableFilter,
        onTableFilterChange: setTableFilter
    }

    return (
        <div className="p-4">
            <div className="flex flex-col md:flex-row gap-5 mb-4">
                <Card className="flex-1 md:max-w-4xl bg-white">
                    <CardHeader>
                        <CardTitle>User Profiles</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="flex flex-col md:flex-row gap-4">
                            {/* Phone Number Group */}
                            <div className="flex flex-col sm:flex-row gap-4 flex-1">
                                <div className="flex-1">
                                    <label htmlFor="phoneNumber" className="text-sm font-medium">Phone Number</label>
                                    <Input
                                        id="phoneNumber"
                                        type="tel"
                                        placeholder="Enter phone number"
                                        value={queryState.phoneNumber}
                                        onChange={handlePhoneNumberChange}
                                        className={phoneError ? 'border-red-500' : ''}
                                        data-testid="phone-number-input"
                                    />
                                    {phoneError && (
                                        <span className="text-sm text-red-500 mt-1">{phoneError}</span>
                                    )}
                                </div>
                                <Button
                                    onClick={handleFindPhoneNumberClick}
                                    disabled={!queryState.phoneNumber || !!phoneError}
                                    className="bg-green-600 hover:bg-green-700 text-white h-10 mt-auto"
                                    data-testid="find-phone-number-button"
                                >
                                    Find
                                </Button>
                            </div>

                            {/* Email Group */}
                            <div className="flex flex-col sm:flex-row gap-4 flex-1">
                                <div className="flex-1">
                                    <label htmlFor="email" className="text-sm font-medium">Email</label>
                                    <Input
                                        id="email"
                                        type="email"
                                        placeholder="Enter email"
                                        value={queryState.email}
                                        onChange={handleEmailChange}
                                        className={emailError ? 'border-red-500' : ''}
                                        data-testid="email-input"
                                    />
                                    {emailError && (
                                        <span className="text-sm text-red-500 mt-1">{emailError}</span>
                                    )}
                                </div>
                                <Button
                                    onClick={handleFindEmailClick}
                                    disabled={!queryState.email || !!emailError}
                                    className="bg-green-600 hover:bg-green-700 text-white h-10 mt-auto"
                                    data-testid="find-email-button"
                                >
                                    Find
                                </Button>
                            </div>

                            {/* UUID Group */}
                            <div className="flex flex-col sm:flex-row gap-4 flex-1">
                                <div className="flex-1">
                                    <label htmlFor="uuid" className="text-sm font-medium">UUID</label>
                                    <Input
                                        id="uuid"
                                        type="text"
                                        placeholder="Enter UUID"
                                        value={queryState.uuid}
                                        onChange={handleUuidChange}
                                        className={uuidError ? 'border-red-500' : ''}
                                        data-testid="uuid-input"
                                    />
                                    {uuidError && (
                                        <span className="text-sm text-red-500 mt-1">{uuidError}</span>
                                    )}
                                </div>
                                <Button
                                    onClick={handleFindUuidClick}
                                    disabled={!queryState.uuid || !!uuidError}
                                    className="bg-green-600 hover:bg-green-700 text-white h-10 mt-auto"
                                    data-testid="find-uuid-button"
                                >
                                    Find
                                </Button>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <div className="flex flex-col gap-2 md:justify-end self-end">
                    <Button
                        onClick={resetToDefaults}
                        variant="outline"
                        className="w-full md:w-auto bg-secondary hover:bg-secondary/90 text-secondary-foreground bg-green-600 text-white hover:bg-green-700"
                    >
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Reset
                    </Button>
                    <Button
                        onClick={handleDownloadCSV}
                        variant="outline"
                        className="w-full md:w-auto bg-primary hover:bg-primary/90 text-primary-foreground bg-green-600 text-white hover:bg-green-700"
                        disabled={isDownloading || loading || !!error || users.length === 0 || queryState.filteredUsers.length === 0}
                        title={hasUnappliedFilters ? "Press Find to apply the new filters" : undefined}
                        data-testid="download-csv-button"
                    >
                        {isDownloading ? (
                            <>
                                <Spinner size="small" className="mr-2" />
                                Downloading...
                            </>
                        ) : (
                            <>
                                <Download className="w-4 h-4 mr-2" />
                                Download CSV
                            </>
                        )}
                    </Button>
                    {hasUnappliedFilters && showUnappliedFiltersHint && (
                        <p className="text-sm text-amber-700" role="status" data-testid="unapplied-filters-hint">
                            Filters changed — press Find to refresh the results before downloading.
                        </p>
                    )}
                </div>
            </div>

            {error ? (
                <AlertView
                    message={error}
                    onAction={() => submittedFilters && submitLookup(submittedFilters)}
                    buttonTitle="Retry"
                />
            ) : loading ? (
                <DataTableSkeleton rows={1} columns={6} />
            ) : (
                <DataTable
                    data={users}
                    columns={columns}
                    onRowClick={(user: UserProfile) => navigate(`/userProfiles/${user.user_uuid}`)}
                    paginationState={paginationState}
                    onDataChange={handleDataChange}
                    tableFilterState={tableFilterState}
                />
            )}
        </div>
    )
}

export default ProfileComponent
